/* WGE Custom Commission production browser integration.
 * Frozen order: validate locally -> core case commit -> render Request ID -> prepare -> PUT -> status.
 * No secret, signed URL, grant, session, status token, file bytes or customer payload is logged or persisted.
 */
(() => {
  'use strict';

  const API_BASE = 'https://api.thewatchtowerops.com';
  const CORE_URL = `${API_BASE}/physical-collectible/commission-requests`;
  const REQUIRED_ORIGIN = 'https://thewatchtowerops.com';
  const MAX_FILES = 5;
  const MAX_TOTAL_BYTES = 40 * 1024 * 1024;
  const CORE_RETRY_DELAYS = [0, 1000, 2000, 5000];
  const PREPARE_RETRY_DELAYS = [0, 1000, 2000, 5000];
  const STATUS_DELAYS = [0, 2000, 3000, 5000, 10000, 15000, 20000];
  const MIME_EXTENSIONS = new Map([
    ['image/jpeg', new Set(['.jpg', '.jpeg'])],
    ['image/png', new Set(['.png'])],
    ['image/webp', new Set(['.webp'])],
    ['application/pdf', new Set(['.pdf'])],
    ['video/mp4', new Set(['.mp4'])],
    ['video/quicktime', new Set(['.mov'])],
  ]);
  const OPTIONAL_FIELDS = [
    'alternate_contact',
    'concept_subject',
    'display_intent_size',
    'budget_range',
    'desired_timing',
    'notes',
  ];
  const instances = new WeakMap();

  class WgeRequestError extends Error {
    constructor(message, { status = 0, code = 'NETWORK_ERROR', fieldErrors = {}, retryable = false } = {}) {
      super(message);
      this.name = 'WgeRequestError';
      this.status = status;
      this.code = code;
      this.fieldErrors = fieldErrors;
      this.retryable = retryable;
    }
  }

  const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  function uuidV4() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    if (!globalThis.crypto?.getRandomValues) throw new WgeRequestError('Secure browser randomness is unavailable.');
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function extensionOf(name) {
    const index = name.lastIndexOf('.');
    return index >= 0 ? name.slice(index).toLowerCase() : '';
  }

  function safeJson(response) {
    return response.json().catch(() => null);
  }

  function publicError(payload, response) {
    const error = payload?.error || {};
    const status = response?.status || 0;
    const code = typeof error.code === 'string' ? error.code : 'REQUEST_FAILED';
    const message = typeof error.message === 'string' && error.message
      ? error.message
      : 'This request could not be processed.';
    const fieldErrors = error.field_errors && typeof error.field_errors === 'object'
      ? error.field_errors
      : {};
    const retryable = Boolean(error.retryable) || status === 429 || status === 500 || status === 503;
    return new WgeRequestError(message, { status, code, fieldErrors, retryable });
  }

  function retryDelay(response, fallback) {
    if (response?.status === 429) {
      const value = Number.parseInt(response.headers.get('Retry-After') || '', 10);
      if (Number.isFinite(value) && value > 0) return value * 1000;
    }
    return fallback;
  }

  async function fetchJsonWithRetry(url, initFactory, delays) {
    let lastError = null;
    for (let attempt = 0; attempt < delays.length; attempt += 1) {
      if (delays[attempt] > 0) await sleep(delays[attempt]);
      let response;
      try {
        response = await fetch(url, initFactory());
      } catch (error) {
        lastError = new WgeRequestError('We could not confirm the request because the network did not respond.', { retryable: true });
        continue;
      }
      const payload = await safeJson(response);
      if (response.ok) return { response, payload };
      const requestError = publicError(payload, response);
      lastError = requestError;
      if (!requestError.retryable || attempt === delays.length - 1) throw requestError;
      const nextIndex = Math.min(attempt + 1, delays.length - 1);
      delays[nextIndex] = retryDelay(response, delays[nextIndex]);
    }
    throw lastError || new WgeRequestError('This request could not be confirmed.', { retryable: true });
  }

  function initialize(root) {
    instances.get(root)?.();

    const form = root.querySelector('[data-wge-cc-form]');
    const submit = root.querySelector('[data-wge-cc-submit]');
    if (!form || !submit || root.dataset.wgeCcMode !== 'production') return;
    // Keep the HTML fallback fail-closed: without this JS guard the dialog method cannot send network data.
    if (form.method !== 'dialog') return;

    const controller = new AbortController();
    const listen = (target, event, handler, options = {}) => {
      target.addEventListener(event, handler, { ...options, signal: controller.signal });
    };

    const controls = Array.from(form.elements).filter((field) =>
      field.matches('input:not([type="file"]), textarea, select'));
    const summary = root.querySelector('[data-wge-cc-error-summary]');
    const fileInput = root.querySelector('[data-wge-cc-files]');
    const fileStatus = root.querySelector('[data-wge-cc-file-status]');
    const confirmation = root.querySelector('[data-wge-cc-confirmation]');
    const preStatus = root.querySelector('[data-wge-cc-submit-status]');
    const result = root.querySelector('[data-wge-cc-result]');
    const requestIdNode = root.querySelector('[data-wge-cc-request-id]');
    const confirmationFiles = root.querySelector('[data-wge-cc-confirmation-files]');
    const messages = Object.freeze({
      idea_description: 'Tell us a little about what you want to create.',
      customer_name: 'Please enter your name.',
      contact_email: 'Please enter a valid email address.',
      country_region: 'Please choose your country or region.',
    });

    if (!summary || !fileInput || !fileStatus || !confirmation || !preStatus || !result || !requestIdNode || !confirmationFiles) return;

    let pendingCore = null;
    let committed = false;

    function setPreStatus(message) {
      preStatus.textContent = message;
    }

    function setResult(message) {
      result.textContent = message;
    }

    function setFileLine(container, text) {
      const item = document.createElement('li');
      item.textContent = text;
      container.append(item);
    }

    function validateControls() {
      const invalid = [];
      controls.forEach((field) => {
        const value = typeof field.value === 'string' ? field.value.trim() : '';
        const missing = field.required && !value;
        const error = root.querySelector(`[data-field-error="${CSS.escape(field.name)}"]`);
        const bad = missing || !field.validity.valid;
        field.removeAttribute('aria-invalid');
        if (error) {
          error.hidden = !bad;
          error.textContent = '';
        }
        if (bad) {
          field.setAttribute('aria-invalid', 'true');
          if (error) {
            error.textContent = missing || field.name === 'contact_email'
              ? messages[field.name]
              : field.validationMessage;
          }
          invalid.push(field);
        }
      });
      summary.hidden = invalid.length === 0;
      if (invalid.length) invalid[0].focus();
      return invalid.length === 0;
    }

    function validateFiles(files) {
      fileStatus.replaceChildren();
      if (files.length > MAX_FILES) {
        setFileLine(fileStatus, `Choose no more than ${MAX_FILES} reference files.`);
        return false;
      }
      let total = 0;
      for (const file of files) {
        const allowed = MIME_EXTENSIONS.get(file.type);
        const extension = extensionOf(file.name);
        if (!file.name || !allowed || !allowed.has(extension) || file.size < 1 || file.size > MAX_TOTAL_BYTES) {
          setFileLine(fileStatus, `${file.name || 'Reference file'} is not an accepted file type or size.`);
          return false;
        }
        total += file.size;
      }
      if (total > MAX_TOTAL_BYTES) {
        setFileLine(fileStatus, 'Reference files must be 40 MiB or less in total.');
        return false;
      }
      files.forEach((file) => setFileLine(fileStatus, `Selected: ${file.name}. Upload begins only after your Request ID is confirmed.`));
      return true;
    }

    function collectSubmission() {
      const files = Array.from(fileInput.files || []);
      if (!validateControls() || !validateFiles(files)) return null;

      const values = Object.fromEntries(controls.map((field) => [field.name, field.value.trim()]));
      const fileIdentity = files.map((file) => ({ name: file.name, type: file.type, size: file.size, lastModified: file.lastModified }));
      const logicalSignature = JSON.stringify({ values, fileIdentity });

      if (pendingCore) {
        if (pendingCore.logicalSignature !== logicalSignature) {
          throw new WgeRequestError('For safety, keep the original values unchanged while retrying this unconfirmed submission. No new request was created by this page.');
        }
        return pendingCore;
      }

      const descriptors = files.map((file) => ({
        client_file_id: uuidV4(),
        display_name: file.name,
        declared_mime: file.type,
        size_bytes: file.size,
      }));
      const payload = {
        schema_version: 1,
        customer_name: values.customer_name,
        contact_email: values.contact_email,
        country_region: values.country_region.toUpperCase(),
        idea_description: values.idea_description,
        reference_files: descriptors,
      };
      OPTIONAL_FIELDS.forEach((name) => {
        payload[name] = values[name] ? values[name] : null;
      });

      pendingCore = {
        logicalSignature,
        coreKey: uuidV4(),
        batchKey: descriptors.length ? uuidV4() : null,
        payload,
        descriptors,
        files,
      };
      return pendingCore;
    }

    function applyFieldErrors(fieldErrors) {
      let first = null;
      Object.entries(fieldErrors || {}).forEach(([name, message]) => {
        const field = controls.find((candidate) => candidate.name === name);
        const error = root.querySelector(`[data-field-error="${CSS.escape(name)}"]`);
        if (field) {
          field.setAttribute('aria-invalid', 'true');
          first ||= field;
        }
        if (error) {
          error.hidden = false;
          error.textContent = typeof message === 'string' ? message : 'Please check this field.';
        }
      });
      summary.hidden = !first;
      first?.focus();
    }

    async function createCore(submission) {
      const body = JSON.stringify(submission.payload);
      return fetchJsonWithRetry(
        CORE_URL,
        () => ({
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Idempotency-Key': submission.coreKey,
          },
          body,
          signal: controller.signal,
        }),
        [...CORE_RETRY_DELAYS],
      );
    }

    function showCoreConfirmation(payload) {
      committed = true;
      confirmation.hidden = false;
      requestIdNode.textContent = payload.request_id;
      setResult(payload.message || 'Your request has reached our team for human review.');
      confirmationFiles.replaceChildren();
      controls.forEach((field) => { field.disabled = true; });
      fileInput.disabled = true;
      submit.disabled = true;
      confirmation.focus();
    }

    async function prepareAttachments(core, submission) {
      const body = JSON.stringify({ schema_version: 1, files: submission.descriptors });
      const url = `${CORE_URL}/${encodeURIComponent(core.request_id)}/attachments/prepare`;
      return fetchJsonWithRetry(
        url,
        () => ({
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${core.attachment_prepare_grant}`,
            'Idempotency-Key': submission.batchKey,
          },
          body,
          signal: controller.signal,
        }),
        [...PREPARE_RETRY_DELAYS],
      );
    }

    async function uploadOne(item, file) {
      if (!item?.upload?.url || item.upload.method !== 'PUT' || !item.upload.required_headers) {
        return false;
      }
      const response = await fetch(item.upload.url, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'omit',
        headers: item.upload.required_headers,
        body: file,
        signal: controller.signal,
      });
      return response.ok;
    }

    async function pollStatus(item, fileName) {
      const url = `${API_BASE}/attachments/${encodeURIComponent(item.attachment_id)}/status?session_id=${encodeURIComponent(item.attachment_session_id)}`;
      let last = 'PENDING';
      for (const delay of STATUS_DELAYS) {
        if (delay) await sleep(delay);
        let response;
        try {
          response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
            headers: {
              'Accept': 'application/json',
              'X-WGE-Attachment-Token': item.status_token,
            },
            signal: controller.signal,
          });
        } catch (_) {
          continue;
        }
        if (!response.ok) continue;
        const payload = await safeJson(response);
        const publicStatus = payload?.public_status;
        if (publicStatus === 'ready') {
          last = 'CLEAN';
          break;
        }
        if (publicStatus === 'rejected') {
          last = 'QUARANTINE';
          break;
        }
        if (publicStatus === 'failed') {
          last = 'FAILED';
          break;
        }
        last = 'PENDING';
      }
      const copy = {
        CLEAN: `${fileName}: received and security checked.`,
        QUARANTINE: `${fileName}: could not be accepted. Your request is still valid.`,
        FAILED: `${fileName}: upload/security processing did not complete. Your request is still valid.`,
        PENDING: `${fileName}: uploaded and still being checked. Your request is already valid.`,
      };
      setFileLine(confirmationFiles, copy[last]);
      return last;
    }

    async function handleAttachments(core, submission) {
      if (!submission.files.length) {
        setFileLine(confirmationFiles, 'No reference files were included.');
        return;
      }
      if (!core.attachment_prepare_allowed || !core.attachment_prepare_grant) {
        setFileLine(confirmationFiles, 'Your request is valid, but references could not be prepared. Please keep your Request ID and do not submit the request again.');
        return;
      }

      let prepared;
      try {
        ({ payload: prepared } = await prepareAttachments(core, submission));
      } catch (_) {
        setFileLine(confirmationFiles, 'Your request is valid, but references could not be prepared. Please keep your Request ID and do not submit the request again.');
        return;
      }

      if (!prepared || prepared.request_id !== core.request_id || !Array.isArray(prepared.attachments)) {
        setFileLine(confirmationFiles, 'Your request is valid, but reference preparation returned an unexpected response. Please keep your Request ID.');
        return;
      }

      const filesById = new Map(submission.descriptors.map((descriptor, index) => [descriptor.client_file_id, submission.files[index]]));
      const preparedById = new Map();
      for (const item of prepared.attachments) {
        if (!item?.client_file_id || preparedById.has(item.client_file_id) || !filesById.has(item.client_file_id)) {
          setFileLine(confirmationFiles, 'Your request is valid, but reference preparation could not be verified. Please keep your Request ID.');
          return;
        }
        preparedById.set(item.client_file_id, item);
      }
      if (preparedById.size !== submission.files.length) {
        setFileLine(confirmationFiles, 'Your request is valid, but not all references were prepared. Please keep your Request ID.');
        return;
      }

      const uploaded = new Set();
      for (const descriptor of submission.descriptors) {
        const item = preparedById.get(descriptor.client_file_id);
        const file = filesById.get(descriptor.client_file_id);
        try {
          if (await uploadOne(item, file)) uploaded.add(descriptor.client_file_id);
        } catch (_) {
          // Bounded recovery below uses the same batch key and never creates a new case.
        }
      }

      const unresolved = submission.descriptors.filter((descriptor) => !uploaded.has(descriptor.client_file_id));
      if (unresolved.length) {
        try {
          const replay = await prepareAttachments(core, submission);
          const replayItems = Array.isArray(replay.payload?.attachments) ? replay.payload.attachments : [];
          const replayById = new Map();
          for (const item of replayItems) {
            if (!item?.client_file_id || replayById.has(item.client_file_id) || !filesById.has(item.client_file_id)) {
              throw new WgeRequestError('Reference recovery could not be verified safely.');
            }
            replayById.set(item.client_file_id, item);
          }
          if (replayById.size !== submission.files.length) {
            throw new WgeRequestError('Reference recovery returned an incomplete attachment set.');
          }
          // Prepare replay rotates the per-attachment status token for every row,
          // so refresh every item even though only unresolved objects are re-uploaded.
          for (const [clientFileId, item] of replayById) preparedById.set(clientFileId, item);
          for (const descriptor of unresolved) {
            const item = replayById.get(descriptor.client_file_id);
            const file = filesById.get(descriptor.client_file_id);
            if (item && await uploadOne(item, file)) uploaded.add(descriptor.client_file_id);
          }
        } catch (_) {
          // Core confirmation remains authoritative even when reference recovery fails.
        }
      }

      for (const descriptor of submission.descriptors) {
        const item = preparedById.get(descriptor.client_file_id);
        if (!uploaded.has(descriptor.client_file_id) || !item) {
          setFileLine(confirmationFiles, `${descriptor.display_name}: upload did not complete. Your request is still valid; keep your Request ID and do not submit again.`);
          continue;
        }
        await pollStatus(item, descriptor.display_name);
      }
    }

    listen(form, 'submit', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (committed) return;

      if (window.location.origin !== REQUIRED_ORIGIN) {
        summary.hidden = false;
        setPreStatus('This request form is available only on the official WatchTower Ops storefront. Nothing has been sent.');
        return;
      }

      let submission;
      try {
        submission = collectSubmission();
      } catch (error) {
        summary.hidden = false;
        setPreStatus(error instanceof Error ? error.message : 'This submission could not be prepared safely.');
        return;
      }
      if (!submission) {
        setPreStatus('Nothing has been sent. Check the highlighted fields and references.');
        return;
      }

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      setPreStatus('Sending your request securely…');

      try {
        const { payload: core } = await createCore(submission);
        if (!core || typeof core.request_id !== 'string' || typeof core.case_state !== 'string' || core.is_order !== false || core.is_accepted !== false) {
          throw new WgeRequestError('The server confirmation could not be verified safely.');
        }
        setPreStatus('Request received. Your Request ID is shown below.');
        showCoreConfirmation(core);
        await handleAttachments(core, submission);
      } catch (error) {
        const requestError = error instanceof WgeRequestError
          ? error
          : new WgeRequestError('We could not confirm this request.');
        if (requestError.fieldErrors && Object.keys(requestError.fieldErrors).length) applyFieldErrors(requestError.fieldErrors);
        summary.hidden = false;
        if (requestError.retryable) {
          setPreStatus('We could not confirm whether the request reached us. Keep this page and your entries unchanged, then press Send again to retry safely with the same submission key.');
        } else {
          setPreStatus(requestError.message || 'This request could not be sent. Nothing has been confirmed.');
          if ([400, 413, 415, 422].includes(requestError.status)) pendingCore = null;
        }
        submit.disabled = false;
      } finally {
        submit.removeAttribute('aria-busy');
      }
    }, { capture: true });

    listen(form, 'input', (event) => {
      const field = event.target;
      if (!controls.includes(field)) return;
      if (field.hasAttribute('aria-invalid')) {
        field.removeAttribute('aria-invalid');
        const error = root.querySelector(`[data-field-error="${CSS.escape(field.name)}"]`);
        if (error) error.hidden = true;
      }
      if (!root.querySelector('[aria-invalid="true"]')) summary.hidden = true;
    });

    listen(fileInput, 'change', () => {
      if (!committed) validateFiles(Array.from(fileInput.files || []));
    });

    submit.disabled = false;

    const faqButtons = Array.from(root.querySelectorAll('[data-wge-cc-faq]'));
    const setFaq = (button, open) => {
      button.setAttribute('aria-expanded', String(open));
      const answer = root.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      if (answer) answer.hidden = !open;
    };
    faqButtons.forEach((button) => {
      setFaq(button, false);
      button.disabled = false;
      listen(button, 'click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        faqButtons.forEach((other) => setFaq(other, other === button && open));
      });
    });

    const chapters = Array.from(root.querySelectorAll('[data-wge-sequence]'));
    const links = Array.from(root.querySelectorAll('[data-wge-cc-chapter]'));
    const cue = root.querySelector('[data-wge-cc-cue]');
    let current = '';
    let chapterObserver;
    const visibleChapters = new Set();
    const updateChapter = (chapter) => {
      if (!chapter || current === chapter.id) return;
      current = chapter.id;
      links.forEach((link) => {
        if (link.hash === `#${current}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      const link = links.find((item) => item.hash === `#${current}`);
      if (cue) {
        cue.querySelector('span').textContent = `${chapter.dataset.wgeSequence.slice(3)} / 08`;
        cue.querySelector('strong').textContent = link?.querySelector('strong')?.textContent || '';
      }
    };
    const chapterForHash = (hash) => chapters.find((chapter) => `#${chapter.id}` === hash);
    const selectVisibleChapter = () => {
      if (!visibleChapters.size) return;
      const anchor = window.innerHeight * 0.34;
      const active = Array.from(visibleChapters).sort((a, b) =>
        Math.abs(a.getBoundingClientRect().top - anchor) - Math.abs(b.getBoundingClientRect().top - anchor))[0];
      updateChapter(active);
    };
    links.forEach((link) => listen(link, 'click', () => updateChapter(chapterForHash(link.hash))));
    listen(window, 'hashchange', () => updateChapter(chapterForHash(window.location.hash)));
    if ('IntersectionObserver' in window) {
      chapterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleChapters.add(entry.target);
          else visibleChapters.delete(entry.target);
        });
        selectVisibleChapter();
      }, { threshold: 0, rootMargin: '-30% 0px -60% 0px' });
      chapters.forEach((chapter) => chapterObserver.observe(chapter));
    }
    updateChapter(chapterForHash(window.location.hash) || chapters[0]);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reveals = Array.from(root.querySelectorAll('[data-wge-cc-reveal]'));
    const motifTargets = Array.from(root.querySelectorAll('.wge-cc__human, .wge-cc__confirmation'));
    const motionTargets = Array.from(new Set([...reveals, ...motifTargets]));
    let motionObserver;
    let resolved = 0;
    const reveal = (element) => {
      element.classList.add('wge-cc__visible');
      if (element.matches('.wge-cc__human, .wge-cc__confirmation')) element.classList.add('wge-cc__motif-visible');
      resolved = Math.max(resolved, Number(element.dataset.wgeCcStage || 0));
      root.style.setProperty('--wge-cc-resolved', String(resolved / 6));
    };
    const setupMotion = () => {
      motionObserver?.disconnect();
      if (reduced.matches || !('IntersectionObserver' in window)) {
        root.classList.remove('wge-cc--motion');
        motionTargets.forEach(reveal);
        return;
      }
      motionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          motionObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
      root.classList.add('wge-cc--motion');
      motionTargets.filter((item) => !item.classList.contains('wge-cc__visible')).forEach((item) => motionObserver.observe(item));
    };
    listen(reduced, 'change', setupMotion);
    setupMotion();

    instances.set(root, () => {
      controller.abort();
      chapterObserver?.disconnect();
      motionObserver?.disconnect();
    });
  }

  const initializePages = () => document.querySelectorAll('[data-wge-cc-page]').forEach(initialize);
  initializePages();
  document.addEventListener('shopify:section:load', initializePages);
})();
